import { useRef, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { PeekClose } from './icons'
import { useAnchoredPlacement, useDismissable, useEntered, type PeekAnchor } from '../../lib/popover'

// ============================================================================
// THE DETAIL PEEK — docs/design/calendar-brief.md → Interactions: "Click a Canvas block →
// detail peek with a link out to the Course page, no edit fields." Extended here to every
// read-only entry the grid can show: a Canvas class, a Canvas due item, a timed Google event,
// and a whole-day Google event. All four are facts somebody else owns, so the peek is a LOOK,
// never an editor — there is no field in it that writes anything, on purpose, the same way a
// Canvas block itself carries no drag handle (grid.tsx, brief rule 1).
//
// A POPOVER, NOT A DIALOG. `role="dialog"` with `aria-modal` traps focus and demands an escape
// hatch that gives it back — the composer earns that because it asks four questions. This asks
// nothing. It is `role="dialog"` WITHOUT `aria-modal`: a screen reader still announces it as a
// distinct region, but Tab is free to leave it, which is what "traps nothing" means in the brief.
//
// ANCHORED, NOT CENTRED, AND NOW ACTUALLY BESIDE THE BLOCK. It is built from the clicked
// element's own `getBoundingClientRect()` — captured once, at click time, by whichever component
// opened it — and placed by `placeAnchored` in popover.ts, which owns the zoom correction that
// used to throw this panel into the bottom-right corner of the screen. See that file's header.
// ============================================================================

export type { PeekAnchor }

export interface PeekContent {
  title: string
  /** The full time range, Google's own "All day", or a Canvas due phrase — never invented. */
  timeRange: string
  /** Null when none was given. Never an empty string standing in for one. */
  location: string | null
  /** Google's own word — rendered ONLY when it is not `confirmed`. Null hides the row entirely. */
  status: 'tentative' | 'cancelled' | null
  /** "From Canvas" or "From Google · synced 6 min ago" — built by the caller, who has the clock. */
  provenance: string
  /** Present on anything derived from a course. Renders "Open in Course" when set. */
  courseSlug: string | null
}

const WIDTH = 252

export function CalendarPeek({
  anchor,
  content,
  onClose,
}: {
  anchor: PeekAnchor
  content: PeekContent
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const entered = useEntered()
  const placement = useAnchoredPlacement(ref, anchor, WIDTH)
  useDismissable(ref, onClose)

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={content.title}
      className={[
        'fixed z-20 flex flex-col gap-[7px] rounded-panel border border-line-2 bg-surface p-[13px] shadow-lifted',
        // THE MOTION SYSTEM'S OWN PANEL BEAT, not a curve invented here: 180ms on `--ease-ink`,
        // the same pair 13a spends on the capture panel's entrance (index.css → `mo-palette`).
        // It used to run on `--dur-fast`/`--ease-out`, the two RESERVED curves that predate the
        // system — which is why a Tasks-lane report found this panel "appearing" while every
        // other surface in the app settles. Same numbers as everything else now.
        'transition-[opacity,transform] duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
        entered ? 'opacity-100' : 'opacity-0',
      ].join(' ')}
      style={{
        width: WIDTH,
        left: placement.left,
        top: placement.top,
        // A small pixel nudge, not a percentage — `left`/`top` are already the corrected, measured
        // position by the time this paints, so the entrance has nothing left to flip.
        transform: entered ? 'translateY(0)' : 'translateY(-4px)',
      }}
    >
      <div className="flex items-start gap-[10px]">
        <span className="min-w-0 flex-1 text-pretty text-[12.5px] font-semibold leading-[1.3] text-ink">
          {content.title}
        </span>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          // 12d's ink hover, the same mechanic every other pill-shaped control in the app uses.
          // The owner's report: "no hover feedback at all" on this panel's buttons.
          style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
          className="mo-ink mo-press flex-none rounded-pill p-[3px] text-faintest-2 hover:text-ink-2"
        >
          <PeekClose />
        </button>
      </div>

      <div className="text-[11px] font-normal leading-[1.4] text-ink-3">{content.timeRange}</div>

      {content.location && (
        <div className="truncate text-[11px] font-normal leading-[1.4] text-ink-3">
          {content.location}
        </div>
      )}

      {/* GOOGLE'S OWN WORD, drawn only when it is not the silent default. A cancelled event never
          reaches here (the range read drops it before either array is built), but the type still
          admits it — the same honesty the all-day row already keeps for whole-day events. */}
      {content.status && (
        <div className="text-[10.5px] font-semibold italic leading-none text-cal-due-ink">
          Marked {content.status} in Google
        </div>
      )}

      <div className="text-[9.5px] font-semibold leading-none tracking-[.04em] text-microcopy">
        {content.provenance}
      </div>

      {content.courseSlug && (
        <Link
          to={`/school/${content.courseSlug}`}
          style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
          className="mo-ink mo-press mt-[2px] self-start rounded-context border border-line px-[10px] py-[5px] text-[10px] font-semibold leading-none text-ink-2"
        >
          Open in Course
        </Link>
      )}
    </div>
  )
}
